import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { ButtonCommonProps } from "./types";
import { KeyColors } from "@/theme/colors";

/**
 * Keyframes for progress animation
 */
const progressAnimation = keyframes`
  0% { 
    width: 0%; 
    border-top-right-radius: 0;
    border-bottom-right-radius: 0; 
  }
  97% {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  100% { 
    width: 100%; 
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px; 
  }
`;

/**
 * Styled Components
 */
interface StyledProgressButtonProps
  extends Omit<ButtonCommonProps, "text" | "onClick"> {
  $borderColor?: KeyColors;
  $textColor?: KeyColors;
  $progressColor?: KeyColors;
  $backgroundColor?: KeyColors;
  $duration?: number;
}

const StyledProgressButtonContainer = styled.button<StyledProgressButtonProps>`
  position: relative;
  display: inline-block;
  transition: all 10s ease;
  cursor: pointer;

  ${({ theme, $backgroundColor, $textColor }) => `
    background: ${
      $backgroundColor ? theme.Colors[$backgroundColor] : theme.Colors.black
    };
    font-family: ${theme.Fonts.ttNormsProBold};
    color: ${$textColor ? theme.Colors[$textColor] : theme.Colors.white};
    font-weight: normal;
    font-size: 16px;
    border-radius: 12px;
    width: 100%;
    height: 50px;
  `}

  &:not(.active):hover {
    opacity: 0.9;
  }
`;

const StyledButton = styled.div<StyledProgressButtonProps>`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  z-index: 10;
  opacity: 1;
  border-radius: 12px;
  color: ${({ theme, $textColor }) =>
    $textColor ? theme.Colors[$textColor] : theme.Colors.white};

  ${({ theme, $textColor }) => `
    color: ${$textColor ? theme.Colors[$textColor] : theme.Colors.white};
  `}
  ${({ theme, $borderColor }) => `
    border: 2px solid ${
      $borderColor ? theme.Colors[$borderColor] : theme.Colors.black
    };
    border-bottom: 5px solid ${
      $borderColor ? theme.Colors[$borderColor] : theme.Colors.black
    };
  `}
`;

const StyledProgress = styled.div<
  Omit<StyledProgressButtonProps, "durationMs"> & {
    $durationSeconds: number;
    $isComplete: boolean;
  }
>`
  width: ${({ $isComplete }) => ($isComplete ? "100%" : "0%")};
  z-index: 5;
  opacity: 0;
  transition: all 0.3s ease;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;

  border-top-right-radius: ${({ $isComplete }) => ($isComplete ? "12px" : "0")};
  border-bottom-right-radius: ${({ $isComplete }) =>
    $isComplete ? "12px" : "0"};

  ${({ theme, $progressColor, $backgroundColor }) => `
    background: ${
      $progressColor ? theme.Colors[$progressColor] : $backgroundColor
    };
  `}

  opacity: 1;
  animation: ${progressAnimation} ${({ $durationSeconds }) => $durationSeconds}s
    linear 0s;
`;

/**
 * ProgressButton Component
 */
interface AutomaticActionButtonProps extends ButtonCommonProps {
  borderColor?: KeyColors;
  backgroundColor?: KeyColors;
  progressColor?: KeyColors;
  durationMs?: number;
  activeByDefault?: boolean;
  onProgressComplete?: () => void;
}

export const AutomaticActionButton = ({
  disabled = false,
  onClick,
  testId,
  text,
  borderColor,
  color,
  backgroundColor,
  progressColor,
  durationMs = 15000,
  ...rest
}: AutomaticActionButtonProps) => {
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userClickedRef = useRef(false);

  const handleClick = () => {
    userClickedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onClick?.();
  };

  useEffect(() => {
    // Start the auto action timer
    timeoutRef.current = setTimeout(() => {
      setIsComplete(true);
      if (!userClickedRef.current) {
        onClick?.();
      }
    }, durationMs);

    // Cleanup on unmount or duration change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [durationMs, onClick]);

  return (
    <StyledProgressButtonContainer
      data-testid={testId}
      onClick={handleClick}
      $borderColor={borderColor}
      $textColor={color}
      $progressColor={progressColor}
      $backgroundColor={backgroundColor}
      disabled={disabled}
      {...rest}
    >
      <StyledButton
        $borderColor={borderColor}
        $textColor={color}
        $progressColor={progressColor}
        $backgroundColor={backgroundColor}
      >
        {text}
      </StyledButton>
      <StyledProgress
        $isComplete={isComplete}
        $borderColor={borderColor}
        $textColor={color}
        $progressColor={progressColor}
        $durationSeconds={durationMs / 1000}
      />
    </StyledProgressButtonContainer>
  );
};
